import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CardMedia,
  Grid,
  Paper,
  Stack,
  Theme,
  Typography,
  SxProps,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import { usePostHog } from 'posthog-js/react';
import { getPaywallLink } from 'features/membership/membership.utils';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { ForcedTheme } from 'components/ForcedTheme';
import { contentVariantFields, ContentVariantFields } from './utils';

export enum BannerPlacement {
  GEO_EVENT = 'geo_event',
  POPUP = 'popup',
}

type SupportUsBannerProps = {
  placement?: BannerPlacement;
  geoEventId?: number;
  onClose: () => void;
};

const IMG_SIZE = 56;

const getSxStyles = ({
  placement,
}: {
  placement: BannerPlacement;
}): Record<string, SxProps<Theme>> => {
  const defaultButtonSx = {
    minHeight: 38,
    pointerEvents: 'auto',
    whiteSpace: 'nowrap',
  };

  return {
    cardMedia: {
      height: IMG_SIZE,
      width: IMG_SIZE,
      minWidth: IMG_SIZE,
      borderRadius: 2,
    },
    contentContainer: {
      alignItems: 'center',
      marginBottom: 2,
    },
    description: {
      fontSize: getResponsiveFontSize('0.875rem'),
    },
    paper: {
      padding: 2,
      ...(placement === BannerPlacement.GEO_EVENT && {
        borderRadius: 2,
        bgcolor: 'background.secondary',
      }),
    },
    textContainer: {
      width: placement === BannerPlacement.POPUP ? 240 : '100%', // Limits banner width when used as a popup
    },
    title: {
      fontWeight: 'bold',
      fontSize: getResponsiveFontSize('1rem'),
      marginBottom: '2px',
    },
    dismissButton: {
      ...defaultButtonSx,
      fontSize: getResponsiveFontSize('0.875rem'),
    },
    supportButton: {
      ...defaultButtonSx,
      fontSize: getResponsiveFontSize('1rem'),
      fontWeight: 'bold',
    },
  };
};

export const SupportUsBanner = (
  props: SupportUsBannerProps,
): JSX.Element | null => {
  const { placement = BannerPlacement.POPUP, geoEventId, onClose } = props;
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [fields, setFields] = useState<ContentVariantFields | null>();

  // Sets the variant shown to the user just once
  useEffect(() => setFields(contentVariantFields()), []);

  useEffect(() => {
    if (geoEventId) {
      logEvent({
        name: ANALYTICS_EVENTS.VIEWED.INCIDENT_BANNER,
        params: {},
      });
      logEvent({
        name: `${ANALYTICS_EVENTS.VIEWED.INCIDENT_BANNER}_${geoEventId}`,
        params: {},
      });

      posthog.capture('viewed_incident_banner', {
        geoEventId,
      });
    } else {
      logEvent({
        name: ANALYTICS_EVENTS.VIEWED.MAP_BANNER,
        params: {},
      });

      posthog.capture('viewed_map_banner', {
        geoEventId,
      });
    }
  }, [posthog, geoEventId]);

  const handleClose = (): void => {
    onClose();

    if (geoEventId) {
      logEvent({
        name: ANALYTICS_EVENTS.CLICKED.INCIDENT_BANNER_CLOSE,
        params: {},
      });
      logEvent({
        name: `${ANALYTICS_EVENTS.CLICKED.INCIDENT_BANNER_CLOSE}_${geoEventId}`,
        params: {},
      });
    } else {
      logEvent({
        name: ANALYTICS_EVENTS.CLICKED.MAP_BANNER_CLOSE,
        params: {},
      });
    }
  };

  const handleClick = (): void => {
    onClose();

    if (geoEventId) {
      logEvent({
        name: ANALYTICS_EVENTS.CLICKED.INCIDENT_BANNER_WAYS_TO_GIVE,
        params: {},
      });
      logEvent({
        name: `${ANALYTICS_EVENTS.CLICKED.INCIDENT_BANNER_WAYS_TO_GIVE}_${geoEventId}`,
        params: {},
      });
    } else {
      logEvent({
        name: ANALYTICS_EVENTS.CLICKED.MAP_BANNER_WAYS_TO_GIVE,
        params: {},
      });
    }
  };

  // Don't render until the fields are set
  if (!fields) return null;

  const sxStyles = getSxStyles({ placement });
  const paywallLink = getPaywallLink({
    hasActiveMembership: false,
    source:
      placement === BannerPlacement.GEO_EVENT
        ? 'incident_banner'
        : 'map_banner',
  });

  const banner = (
    <Paper elevation={0} sx={sxStyles.paper} data-testid="support-us-banner">
      <Stack direction="row" spacing={2} sx={sxStyles.contentContainer}>
        <CardMedia image={fields.imgSrc} sx={sxStyles.cardMedia} />
        <Box sx={sxStyles.textContainer}>
          <Typography sx={sxStyles.title} variant="h6">
            {t(fields.title)}
          </Typography>
          <Typography variant="body2" sx={sxStyles.description}>
            {t(fields.description)}
          </Typography>
        </Box>
      </Stack>

      <Grid spacing={1} container>
        <Grid size="auto">
          <Button
            color="neutral"
            data-testid="support-us-banner-dismiss"
            fullWidth
            sx={sxStyles.dismissButton}
            onClick={handleClose}
          >
            {t(fields.dismiss)}
          </Button>
        </Grid>
        <Grid size="grow">
          <Button
            component={Link}
            fullWidth
            to={{
              pathname: paywallLink.pathname,
              search: paywallLink.search,
              state: {
                fromRoute: window.location.pathname + window.location.search,
              },
            }}
            onClick={handleClick}
            sx={sxStyles.supportButton}
          >
            {t(fields.support)}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  if (placement === BannerPlacement.GEO_EVENT) {
    return <ForcedTheme mode="dark">{banner}</ForcedTheme>;
  }

  return banner;
};
